import { NoProjectSelectedScreen } from "./NoProjectSelectedScreen";
import { Drawer, Layout } from "antd";
import { SideBar } from "./playground-left-sidebar/SideBar";
import { VidContainer } from "./MainVidPlayer/VidContainer";
import { SchemaEditors } from "./RightPanel/SchemaEditors";
import { TimelinePanel } from "./TimelinePanel";
import { useContext, useEffect, useRef } from "react";
import { MSTContext } from "../stores/main";
import { observer } from "mobx-react-lite";
import FlowPage from "../pages/flow/FlowPage";
import "./MainLayout.scss";
import { SceneListSidebar } from "./SceneSidebar/SceneListSidebar";
import { useUndoRedoShortcuts } from "../hooks/useUndoRedoShortcuts";
import { usePlaybackShortcuts } from "../hooks/usePlaybackShortcuts";
import { useLayerDeletionShortcuts } from "../hooks/useLayerDeletionShortcuts";
import { useLayerClipboardShortcuts } from "../hooks/useLayerClipboardShortcuts";
import { useLayerSortingShortcuts } from "../hooks/useLayerSortingShortcuts";
import { useLayerNudgeShortcuts } from "../hooks/useLayerNudgeShortcuts";
type Props = {};
const { Content } = Layout;

function dismissPortaledPopups(): void {
  if (typeof document === "undefined" || !document.body) return;

  const eventInit: MouseEventInit = {
    bubbles: true,
    cancelable: true,
    view: window,
  };

  if (typeof PointerEvent !== "undefined") {
    document.body.dispatchEvent(new PointerEvent("pointerdown", eventInit));
  } else {
    document.body.dispatchEvent(new MouseEvent("pointerdown", eventInit));
  }

  document.body.dispatchEvent(new MouseEvent("mousedown", eventInit));
  document.body.dispatchEvent(new MouseEvent("click", eventInit));
}

export const MainLayout = observer((props: Props) => {
  // Set up undo/redo keyboard shortcuts (Cmd+Z, Cmd+Shift+Z)
  useUndoRedoShortcuts();
  // Set up playback keyboard shortcuts (Space for play/pause)
  usePlaybackShortcuts();
  // Set up layer deletion keyboard shortcuts (Delete/Backspace)
  useLayerDeletionShortcuts();
  // Set up layer clipboard keyboard shortcuts (Cmd/Ctrl+C, Cmd/Ctrl+V)
  useLayerClipboardShortcuts();
  // Set up layer sorting keyboard shortcuts (Cmd/Ctrl+[ and Cmd/Ctrl+])
  useLayerSortingShortcuts();
  // Set up layer nudge keyboard shortcuts (Arrow keys)
  useLayerNudgeShortcuts();

  const {
    platformStore: {
      selectedProjectId,
      allProjects,
      initialProjectLoadSuccess,
      selectedVideoPart,
      projectWorkspaceVersion,
    },
    layoutStore: { schemaDrawerOpen, setSchemaDrawerOpen },
    setCurrentTab,
    currentTab,
  } = useContext(MSTContext);
  const wasSchemaDrawerOpenRef = useRef(schemaDrawerOpen);
  const selectedProjectExists = allProjects.find(
    (project) => project.id === selectedProjectId
  );

  useEffect(() => {
    if (wasSchemaDrawerOpenRef.current && !schemaDrawerOpen) {
      dismissPortaledPopups();
    }

    wasSchemaDrawerOpenRef.current = schemaDrawerOpen;
  }, [schemaDrawerOpen]);

  if (selectedProjectId && !currentTab) {
    setCurrentTab("layers");
  }

  const getCurrentView = () => {
    switch (currentTab) {
      case "layers":
        return (
          <Content>
            <div className="layers-content">
              <div className="layers-main-wrapper">
                <SideBar />
                <VidContainer />
              </div>
              <TimelinePanel />
            </div>
          </Content>
        );
      case "flow":
        return (
          <Content className="flow-content">
            <FlowPage />
          </Content>
        );
      case "thumb":
        return null;
      default:
        return (
          <>
            <SideBar />
            <Content>
              <VidContainer />
            </Content>
          </>
        );
    }
  };

  return !selectedProjectId ? (
    <NoProjectSelectedScreen
      hasProjects={!initialProjectLoadSuccess || !!allProjects.length}
      projectExists={true}
    />
  ) : selectedProjectExists !== undefined ? (
    <div className="main-layout-wrapper">
      <SceneListSidebar />
      <Layout
        className={"main-layout"}
        style={{ background: "var(--background)" }}
      >
        {getCurrentView()}
      </Layout>
      <Drawer
        placement="right"
        open={schemaDrawerOpen}
        onClose={() => setSchemaDrawerOpen(false)}
        mask={false}
        width={360}
        rootClassName="schema-drawer"
        styles={{
          body: { padding: 0, background: "var(--background)" },
          header: { display: "none" },
        }}
      >
        <SchemaEditors />
      </Drawer>
      {!selectedVideoPart && !projectWorkspaceVersion?.videoParts && (
        <div
          tabIndex={-1}
          className="main-layout-wrapper-loading"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        ></div>
      )}
    </div>
  ) : (
    <NoProjectSelectedScreen
      hasProjects={!initialProjectLoadSuccess || !!allProjects.length}
      projectExists={selectedProjectExists !== undefined}
    />
  );
});
